import { useQuery } from "react-query";
import { useAuthContext } from "context/AuthContext";
import {
    getAcctOverview, 

  getAccountCount,
  getAllEmailCampaignRevenue,
  getCampaignRevenue,
  getEmailMetrics,
  getInterfacePie,
  getPerformancePie,
  getSenderPie,
  getStatusTrendGraph,
  getSummaryPanel,
  getSummaryRevenue,
  getSummaryRevenueForVoice,
  getTemplateDistribution,
  getUsersCount,
  getVolumetricsPanel,
  getWAABusinessNumCounter,
  getWAATemplateAnalyticsApi,
  getWASummaryPanel,
  getWhatsAppRevenueTrend,
  getWhatsAppStatusTrend,
  getWhatsAppTemplateTypes,
  getWhatsAppTotalRevenue,
  getSummarySmsRevenueData,
  getEmailVolPanelData,
  getWhatsAppInboundSummary,
  getAuthConfigMetricsCount,
} from "lib/dashboard-apis";

const _options = {
  staleTime: 5 * 60 * 1000,
  retry: 3,
  retryDelay: 2 * 1000,
};

// Generic reusable hook
const useDashboardQuery = (key, apiFn, { qKeys = [], options = {} }) => {
  const { userDetails } = useAuthContext();

  return useQuery(
    [key, userDetails?.token, ...qKeys],
    apiFn,
    { ..._options, ...options } // merge default + custom
  );
};

/**
 * Common args for all dashboard hooks
 *
 * @typedef {Object} DashboardHookArgs
 * @property {Array} [qKeys]   - Extra query keys to differentiate cache
 * @property {Object} [options] - Extra react-query options
 */

// Thin wrappers
export const useAcctOverview = (arg) =>
  useDashboardQuery("accountOverview", getAcctOverview, arg);

/**
 * Hook to get volumetrics panel
 * @param {DashboardHookArgs} arg
 */
export const useVolumetricsPanel = (arg) =>
  useDashboardQuery("volumetricsPanel", getVolumetricsPanel, arg);

/**
 * Hook to get campaign revenue
 * @param {DashboardHookArgs} arg
 */
export const useCampaignRevenue = (arg) =>
  useDashboardQuery("revenueData", getCampaignRevenue, arg);
export const useSummarySmsRevenueData = (arg) =>
  useDashboardQuery("SummarySmsRevenueData", getSummarySmsRevenueData, arg);

/**
 * Hook to get accounts count
 * @param {DashboardHookArgs} arg
 */
export const useAccountsCountData = (arg) =>
  useDashboardQuery("accountsCount", getAccountCount, arg);

/**
 * Hook to get users count
 * @param {DashboardHookArgs} arg
 */
export const useUsersCount = (arg) =>
  useDashboardQuery("usersCount", getUsersCount, arg);

/**
 * Hook to get all channel summary panel
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelSummaryPanelData = (arg) =>
  useDashboardQuery("summaryPanel", getSummaryPanel, arg);


export const useEmailVolPanelData = (arg) =>
  useDashboardQuery("getEmailVolPanelData", getEmailVolPanelData, arg);

/**
 * Hook to get whatsapp total revenue
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelWaRevenueData = (arg) =>
  useDashboardQuery("getWhatsAppTotalRevenue", getWhatsAppTotalRevenue, arg);

/**
 * Hook to get Whatsapp summary panel
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelWaSummaryPanelData = (arg) =>
  useDashboardQuery("waSummaryPanel", getWASummaryPanel, arg);

/**
 * Hook to get senders pie chart data
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelSenderPieData = (arg) =>
  useDashboardQuery("getSenderPie", getSenderPie, arg);

/**
 * Hook to get interface pie chart data
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelInterFacePieData = (arg) =>
  useDashboardQuery("interfacePie", getInterfacePie, arg);

/**
 * Hook to get performance pie chart data
 * @param {DashboardHookArgs} arg
 */
export const usePerformancePieData = (arg) =>
  useDashboardQuery("performancePie", getPerformancePie, arg);

/**
 * Hook to get summary revenue
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelSummaryRevenueData = (arg) =>
  useDashboardQuery("getSummaryRevenue", getSummaryRevenue, arg);

/**
 * Hook to get email revenue
 * @param {DashboardHookArgs} arg
 */
export const useEmailCampRevenueData = (arg) =>
  useDashboardQuery("emailrevenueData", getAllEmailCampaignRevenue, arg);
/**
 * Hook to get voice summary revenue
 * @param {DashboardHookArgs} arg
 */
export const useAllChannelSummaryRevenueVoiceData = (arg) =>
  useDashboardQuery(
    "getSummaryRevenueForVoice",
    getSummaryRevenueForVoice,
    arg
  );

/**
 * Hook to get dashboard status trend
 * @param {DashboardHookArgs} arg
 */
export const useStatusTrendData = (arg) =>
  useDashboardQuery("getStatusTrend", getStatusTrendGraph, arg);

/**
 * Hook to get email metrics
 * @param {DashboardHookArgs} arg
 */
export const useEmailMetricsData = (arg) =>
  useDashboardQuery("emailMetricsData", getEmailMetrics, arg);

/**
 * Hook to get whatsapp account business number counter
 * @param {DashboardHookArgs} arg
 */
export const useWaABusinessNumCounter = (arg) =>
  useDashboardQuery("WaABusinessNumCounter", getWAABusinessNumCounter, arg);

/**
 * Hook to get whatsapp account template analytics data
 * @param {DashboardHookArgs} arg
 */
export const useWaATemplateAnalyticsApi = (arg) =>
  useDashboardQuery("WAATemplateAnalyticsRes", getWAATemplateAnalyticsApi, arg);

/**
 * Hook to get whatsapp template distribution data
 * @param {DashboardHookArgs} arg
 */
export const useWaTemplateDistribution = (arg) =>
  useDashboardQuery("WaTemplateDistribution", getTemplateDistribution, arg);
/**
 * Hook to get whatsapp template types data
 * @param {DashboardHookArgs} arg
 */
export const useWaTemplateTypes = (arg) =>
  useDashboardQuery("WaTemplateTypes", getWhatsAppTemplateTypes, arg);

/**
 * Hook to get whatsapp revenue | usage trend data
 * @param {DashboardHookArgs} arg
 */
export const useWhatsAppRevenueTrend = (arg) =>
  useDashboardQuery("getWhatsAppRevenueTrend", getWhatsAppRevenueTrend, arg);

/**
 * Hook to get whatsapp message trend data
 * @param {DashboardHookArgs} arg
 */
export const useWhatsAppMessageTrend = (arg) =>
  useDashboardQuery("getWhatsAppMessageTrend", getWhatsAppStatusTrend, arg);

/**
 * Hook to get whatsapp inbound summary data
 * @param {DashboardHookArgs} arg
 */
export const useWhatsAppInboundSummary = (arg) =>
  useDashboardQuery("getWhatsAppInboundSummary", getWhatsAppInboundSummary, arg);

/**
 * Hook to get authconfig (OTP) metrics count
 * @param {DashboardHookArgs} arg
 */
export const useAuthConfigMetrics = (arg) =>
  useDashboardQuery("getAuthConfigMetricsCount", getAuthConfigMetricsCount, arg);
